import { useState, useEffect } from 'react';
import { useWorkDocumentStore } from '../../../store/workDocumentStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, FileText, Folder, CheckCircle, Clock, Trash2 } from 'lucide-react';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import toast from 'react-hot-toast';

const FOLDERS = ['engineering', 'product', 'design', 'marketing', 'general'];
const TYPES = ['wiki', 'spec', 'guide', 'policy', 'other'];
const STATUSES = ['draft', 'published'];
const EMPTY_FORM = { title: '', content: '', folder: 'engineering', type: 'wiki', status: 'draft' };

export function WorkDocuments() {
    const { documents, isLoading, fetchDocuments, createDocument, deleteDocument } = useWorkDocumentStore();
    const [search, setSearch] = useState('');
    const [isFormOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const filteredDocuments = documents.filter(d =>
        d.title.toLowerCase().includes(search.toLowerCase()) ||
        d.folder.toLowerCase().includes(search.toLowerCase())
    );

    const openCreate = () => { setForm(EMPTY_FORM); setErrors({}); setFormOpen(true); };

    const handleSubmit = async () => {
        const next: Record<string, string> = {};
        if (!form.title.trim()) next.title = 'Title is required';
        if (!form.content.trim()) next.content = 'Content is required';
        setErrors(next);
        if (Object.keys(next).length) return;
        setSaving(true);
        try {
            await createDocument({
                title: form.title.trim(), content: form.content.trim(),
                folder: form.folder, type: form.type, status: form.status,
            });
            toast.success('Document created');
            setFormOpen(false);
        } catch (e) {
            toast.error('Failed to create document');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteDocument(deleteTarget.id);
            toast.success('Document deleted');
            setDeleteTarget(null);
        } catch (e) {
            toast.error('Failed to delete document');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6 flex flex-col h-[calc(100vh-8rem)]">
            <div className="flex justify-between items-center shrink-0">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Documents</h1>
                    <p className="text-muted-foreground">Collaborative wikis and specifications.</p>
                </div>
                <Button variant="primary" onClick={openCreate}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Document
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 flex-1 min-h-0">
                <div className="md:col-span-1 space-y-4 flex flex-col hidden md:flex">
                    <Card className="flex-1 flex flex-col">
                        <CardHeader>
                            <h3 className="text-foreground font-display text-card-title">Folders</h3>
                        </CardHeader>
                        <CardContent className="space-y-1 flex-1 overflow-y-auto">
                            <Button variant="ghost" className="w-full justify-start font-medium bg-secondary/50">
                                <Folder className="w-4 h-4 mr-2" /> All Documents
                            </Button>
                            <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                                <Folder className="w-4 h-4 mr-2" /> Engineering
                            </Button>
                            <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                                <Folder className="w-4 h-4 mr-2" /> Product
                            </Button>
                            <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                                <Folder className="w-4 h-4 mr-2" /> Design
                            </Button>
                            <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                                <Folder className="w-4 h-4 mr-2" /> Marketing
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                <div className="md:col-span-3 flex flex-col space-y-4 min-h-0">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 shrink-0">
                        <Card>
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Total Docs</p>
                                    <h3 className="text-foreground font-display text-metric">
                                        {documents.length}
                                    </h3>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                                    <Clock className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Drafts</p>
                                    <h3 className="text-foreground font-display text-metric">
                                        {documents.filter(d => d.status === 'draft').length}
                                    </h3>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                                    <CheckCircle className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Published</p>
                                    <h3 className="text-foreground font-display text-metric">
                                        {documents.filter(d => d.status === 'published').length}
                                    </h3>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <Card className="flex-1 flex flex-col min-h-0">
                        <CardHeader className="shrink-0 border-b border-border">
                            <div className="flex justify-between items-center">
                                <h3 className="text-foreground font-display text-section-title">Document List</h3>
                                <div className="relative w-64">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input 
                                        placeholder="Search documents..." 
                                        className="pl-9"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 overflow-auto">
                            {isLoading ? (
                                <div className="p-8 text-center text-muted-foreground">Loading...</div>
                            ) : (
                                <table className="w-full text-sm text-left relative">
                                    <thead className="bg-secondary/50 text-muted-foreground font-semibold sticky top-0">
                                        <tr>
                                            <th className="px-6 py-3">Title</th>
                                            <th className="px-6 py-3">Folder</th>
                                            <th className="px-6 py-3">Type</th>
                                            <th className="px-6 py-3">Status</th>
                                            <th className="px-6 py-3">Last Modified</th>
                                            <th className="px-6 py-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {filteredDocuments.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                                                    No documents found.
                                                </td>
                                            </tr>
                                        ) : filteredDocuments.map(d => (
                                            <tr key={d.id} className="hover:bg-secondary/20 transition-colors">
                                                <td className="px-6 py-4 font-medium text-foreground">
                                                    {d.title}
                                                </td>
                                                <td className="px-6 py-4 capitalize">{d.folder}</td>
                                                <td className="px-6 py-4 capitalize">{d.type}</td>
                                                <td className="px-6 py-4">
                                                    <Badge variant={d.status === 'published' ? 'success' : 'secondary'}>
                                                        {d.status.toUpperCase()}
                                                    </Badge>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {new Date(d.updatedAt).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <Button variant="ghost" size="icon-sm" aria-label="Delete document"
                                                        className="text-muted-foreground hover:text-destructive"
                                                        onClick={() => setDeleteTarget({ id: d.id, title: d.title })}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Create document modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New Document</DialogTitle>
                        <DialogDescription>Create a wiki page or specification.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Title</label>
                            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="e.g. API Design Spec" invalid={!!errors.title} />
                            {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Folder</label>
                                <Select value={form.folder} onValueChange={(v) => setForm(f => ({ ...f, folder: v }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{FOLDERS.map(x => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Type</label>
                                <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{TYPES.map(x => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Status</label>
                                <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUSES.map(x => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Content</label>
                            <Textarea rows={8} value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
                                placeholder="Write the document…" invalid={!!errors.content} />
                            {errors.content && <p className="text-xs text-destructive">{errors.content}</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleSubmit} isLoading={saving}>Create Document</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete document</DialogTitle>
                        <DialogDescription>
                            Delete <span className="font-medium text-foreground">{deleteTarget?.title}</span>? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
